import React, { useState, useEffect } from 'react';
import { 
  FileText, CheckCircle, Lock, AlertCircle, ExternalLink, 
  Code, Lightbulb, Users, Target, Rocket, Github, Video, Globe,
  ChevronRight, ChevronDown, Clock, X, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Problem Statement Detail Modal
const ProblemStatementDetailModal = ({ problem, onClose, onSelect, isSelected }) => {
  if (!problem) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Problem Statement Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Title & Badges */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{problem.title}</h3>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {problem.theme}
              </span>
              {problem.difficulty && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  problem.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                  problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {problem.difficulty.charAt(0).toUpperCase() + problem.difficulty.slice(1)}
                </span>
              )}
            </div>
          </div>

          {/* Full Description */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <FileText size={18} className="text-blue-600" />
              Problem Description
            </h4>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{problem.description}</p>
            </div>
          </div>

          {/* Scope (if available) */}
          {problem.scope && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Target size={18} className="text-purple-600" />
                Scope
              </h4>
              <div className="bg-purple-50 p-4 rounded-xl">
                <p className="text-gray-700 whitespace-pre-wrap">{problem.scope}</p>
              </div>
            </div>
          )}

          {/* Requirements (if available) */}
          {problem.requirements && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Requirements
              </h4>
              <div className="bg-green-50 p-4 rounded-xl">
                <p className="text-gray-700 whitespace-pre-wrap">{problem.requirements}</p>
              </div>
            </div>
          )}

          {/* Expected Outcomes (if available) */}
          {problem.expected_outcomes && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Rocket size={18} className="text-orange-600" />
                Expected Outcomes
              </h4>
              <div className="bg-orange-50 p-4 rounded-xl">
                <p className="text-gray-700 whitespace-pre-wrap">{problem.expected_outcomes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          {!isSelected && (
            <button
              onClick={() => {
                onSelect(problem.id);
                onClose();
              }}
              className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Lock size={18} />
              Select This Problem
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Problem Statement Selection Component
const ProblemStatementSelector = ({ team, onSelect, disabled }) => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [isOpenInnovation, setIsOpenInnovation] = useState(false);
  const [teamIdea, setTeamIdea] = useState(null);
  const [viewDetailsProblem, setViewDetailsProblem] = useState(null);

  useEffect(() => {
    fetchProblemStatements();
  }, []);

  const fetchProblemStatements = async () => {
    try {
      const response = await axios.get(`${API}/team/available-problem-statements`);
      setStatements(response.data.problem_statements || []);
      setIsOpenInnovation(response.data.is_open_innovation || false);
      setTeamIdea(response.data.team_idea);
    } catch (err) {
      toast.error('Failed to fetch problem statements');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromModal = async (problemId) => {
    try {
      await axios.post(`${API}/team/select-problem-statement`, {
        problem_statement_id: problemId
      });
      toast.success('Problem statement selected and locked!');
      onSelect();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to select problem statement');
    }
  };

  const handleSelect = async () => {
    if (isOpenInnovation) {
      try {
        await axios.post(`${API}/team/confirm-open-innovation`);
        toast.success('Open Innovation idea confirmed as problem statement');
        onSelect();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to confirm');
      }
    } else {
      if (!selectedId) {
        toast.error('Please select a problem statement');
        return;
      }
      try {
        await axios.post(`${API}/team/select-problem-statement`, {
          problem_statement_id: selectedId
        });
        toast.success('Problem statement selected and locked!');
        onSelect();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to select problem statement');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
        <Lock className="mx-auto text-green-600 mb-3" size={32} />
        <h3 className="font-bold text-green-800">Problem Statement Locked</h3>
        <p className="text-sm text-green-600 mt-1">Your problem statement selection is confirmed</p>
      </div>
    );
  }

  return (
    <>
      {/* Problem Detail Modal */}
      {viewDetailsProblem && (
        <ProblemStatementDetailModal
          problem={viewDetailsProblem}
          onClose={() => setViewDetailsProblem(null)}
          onSelect={handleSelectFromModal}
          isSelected={selectedId === viewDetailsProblem.id}
        />
      )}

      <div className="space-y-4" data-testid="problem-statement-selector">
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-amber-800">Important: Selection is Permanent</p>
            <p className="text-sm text-amber-700">Once you select a problem statement, it cannot be changed. Choose carefully!</p>
          </div>
        </div>
      </div>

      {isOpenInnovation ? (
        <div className="rounded-xl border-2 border-purple-300 overflow-hidden bg-gradient-to-br from-purple-50 to-indigo-50">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Lightbulb className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">Open Innovation - Your Custom Idea</h3>
                <p className="text-sm text-purple-100">Your registered idea will become your problem statement</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileText size={16} className="text-purple-600" />
                <h4 className="font-semibold text-gray-800">Your Idea Description</h4>
              </div>
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{teamIdea?.description || 'No description found'}</p>
              </div>
            </div>
            
            <button
              onClick={handleSelect}
              className="w-full py-3.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-purple-200"
              data-testid="confirm-open-innovation-btn"
            >
              <Lock size={20} />
              Confirm & Lock This as My Problem Statement
            </button>
          </div>
        </div>
      ) : (
        <>
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Target className="text-blue-600" size={20} />
            Select Problem Statement for "{team?.hackathon_theme}"
          </h3>
          
          {statements.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
              <FileText className="mx-auto text-gray-400 mb-3" size={40} />
              <p className="text-gray-500">No problem statements available for your theme yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {statements.map((ps) => (
                <div
                  key={ps.id}
                  className={`rounded-xl border-2 transition-all overflow-hidden ${
                    selectedId === ps.id 
                      ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md bg-white'
                  }`}
                  data-testid={`problem-statement-${ps.id}`}
                >
                  {/* Card Header */}
                  <div 
                    className={`p-4 cursor-pointer ${selectedId === ps.id ? 'bg-blue-100' : 'hover:bg-gray-50'}`}
                    onClick={() => setSelectedId(ps.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          {/* Selection Radio */}
                          <div 
                            className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedId === ps.id 
                                ? 'border-blue-500 bg-blue-500' 
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {selectedId === ps.id && <CheckCircle className="text-white" size={14} />}
                          </div>
                          
                          {/* Title & Badges */}
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 text-lg leading-tight">{ps.title}</h4>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                {ps.theme}
                              </span>
                              {ps.difficulty && (
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  ps.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                  ps.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {ps.difficulty.charAt(0).toUpperCase() + ps.difficulty.slice(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Description Preview */}
                        <p className="text-sm text-gray-600 mt-3 ml-8 line-clamp-2 leading-relaxed">
                          {ps.description}
                        </p>
                      </div>

                      {/* View Details Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewDetailsProblem(ps);
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors flex-shrink-0"
                        data-testid={`view-details-${ps.id}`}
                      >
                        <Eye size={16} />
                        <span className="hidden sm:inline">View Details</span>
                      </button>
                    </div>
                  </div>

                  {/* Expanded Preview for Selected */}
                  {selectedId === ps.id && ps.scope && (
                    <div className="px-4 pb-4 ml-8">
                      <div className="bg-white p-3 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Scope Preview</p>
                        <p className="text-sm text-gray-700 line-clamp-2">{ps.scope}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {statements.length > 0 && (
            <button
              onClick={handleSelect}
              disabled={!selectedId}
              className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                selectedId 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="select-problem-btn"
            >
              <Lock size={20} />
              Lock Selection & Continue
            </button>
          )}
        </>
      )}
    </div>
    </>
  );
};

// Round 1 Submission Form
const Round1Form = ({ team, onSubmit, submission, roundOpen }) => {
  const [formData, setFormData] = useState({
    member_expertise: team?.members?.map(m => ({ member_email: m.email, expertise: '' })) || [],
    business_scope: '',
    social_impact: '',
    tech_stack: []
  });
  const [techInput, setTechInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleExpertiseChange = (email, value) => {
    setFormData(prev => ({
      ...prev,
      member_expertise: prev.member_expertise.map(me => 
        me.member_email === email ? { ...me, expertise: value } : me
      )
    }));
  };

  const addTech = () => {
    if (techInput.trim() && !formData.tech_stack.includes(techInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tech_stack: [...prev.tech_stack, techInput.trim()]
      }));
      setTechInput('');
    }
  };

  const removeTech = (tech) => {
    setFormData(prev => ({
      ...prev,
      tech_stack: prev.tech_stack.filter(t => t !== tech)
    }));
  };

  const handleSubmit = async () => {
    // Validate
    if (formData.member_expertise.some(me => !me.expertise.trim())) {
      toast.error('Please fill expertise for all team members');
      return;
    }
    if (formData.business_scope.length < 50) {
      toast.error('Business scope must be at least 50 characters');
      return;
    }
    if (formData.social_impact.length < 50) {
      toast.error('Social impact must be at least 50 characters');
      return;
    }
    if (formData.tech_stack.length === 0) {
      toast.error('Please add at least one technology');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/team/submit/round1`, formData);
      toast.success('Round 1 submitted successfully!');
      onSubmit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Already submitted - show submitted state
  if (submission) {
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
        <CheckCircle className="mx-auto text-green-600 mb-3" size={32} />
        <h3 className="font-bold text-green-800">Round 1 Submitted</h3>
        <p className="text-sm text-green-600 mt-1">Your basic details have been recorded</p>
      </div>
    );
  }

  // Round not open - show closed
  if (!roundOpen) {
    return (
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
        <Clock className="mx-auto text-gray-400 mb-3" size={32} />
        <h3 className="font-bold text-gray-600">Round 1 Not Open</h3>
        <p className="text-sm text-gray-500 mt-1">Submissions for this round are currently closed</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="round1-form">
      {/* Member Expertise */}
      <div>
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Users size={18} className="text-blue-600" />
          Team Member Expertise
        </h4>
        <div className="space-y-3">
          {team?.members?.map((member, idx) => (
            <div key={member.email} className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {member.name} ({member.email})
              </label>
              <textarea
                value={formData.member_expertise.find(me => me.member_email === member.email)?.expertise || ''}
                onChange={(e) => handleExpertiseChange(member.email, e.target.value)}
                placeholder="Describe skills, experience, and role in the project..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={2}
                data-testid={`expertise-${idx}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Business Scope */}
      <div>
        <label className="block font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Rocket size={18} className="text-purple-600" />
          Business Scope
        </label>
        <textarea
          value={formData.business_scope}
          onChange={(e) => setFormData(prev => ({ ...prev, business_scope: e.target.value }))}
          placeholder="Describe the business potential, target market, revenue model, and scalability of your solution... (min 50 characters)"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          rows={4}
          data-testid="business-scope"
        />
        <p className="text-xs text-gray-500 mt-1">{formData.business_scope.length}/2000 characters</p>
      </div>

      {/* Social Impact */}
      <div>
        <label className="block font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Target size={18} className="text-green-600" />
          Social Impact/Scope
        </label>
        <textarea
          value={formData.social_impact}
          onChange={(e) => setFormData(prev => ({ ...prev, social_impact: e.target.value }))}
          placeholder="Describe how your solution addresses social challenges, benefits communities, or contributes to sustainability... (min 50 characters)"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
          rows={4}
          data-testid="social-impact"
        />
        <p className="text-xs text-gray-500 mt-1">{formData.social_impact.length}/2000 characters</p>
      </div>

      {/* Tech Stack */}
      <div>
        <label className="block font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Code size={18} className="text-orange-600" />
          Tech Stack
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={techInput}
            onChange={(e) => setTechInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTech())}
            placeholder="Add technology (e.g., React, Python, TensorFlow)"
            className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            data-testid="tech-input"
          />
          <button
            type="button"
            onClick={addTech}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.tech_stack.map((tech) => (
            <span
              key={tech}
              className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm"
            >
              {tech}
              <button
                type="button"
                onClick={() => removeTech(tech)}
                className="hover:text-orange-900"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="submit-round1-btn"
      >
        {submitting ? (
          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <>
            <CheckCircle size={20} />
            Submit Round 1
          </>
        )}
      </button>
    </div>
  );
};

// Round 2/3 Submission Form
const TechnicalSubmissionForm = ({ roundNumber, onSubmit, submission, roundOpen, previousRoundComplete }) => {
  const [formData, setFormData] = useState({
    github_url: '',
    demo_url: '',
    video_url: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validate
    if (!formData.github_url) {
      toast.error('GitHub repository URL is required');
      return;
    }
    if (!formData.video_url) {
      toast.error('Video URL is required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/team/submit/round${roundNumber}`, formData);
      toast.success(`Round ${roundNumber} submitted successfully!`);
      onSubmit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Already submitted - show submitted state
  if (submission) {
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
        <CheckCircle className="mx-auto text-green-600 mb-3" size={32} />
        <h3 className="font-bold text-green-800">Round {roundNumber} Submitted</h3>
        <p className="text-sm text-green-600 mt-1">Your technical submission has been recorded</p>
      </div>
    );
  }

  // Previous round not complete - show locked
  if (!previousRoundComplete) {
    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
        <Lock className="mx-auto text-amber-600 mb-3" size={32} />
        <h3 className="font-bold text-amber-800">Round {roundNumber - 1} Required</h3>
        <p className="text-sm text-amber-600 mt-1">Complete Round {roundNumber - 1} to unlock this submission</p>
      </div>
    );
  }

  // Round not open - show closed
  if (!roundOpen) {
    return (
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
        <Clock className="mx-auto text-gray-400 mb-3" size={32} />
        <h3 className="font-bold text-gray-600">Round {roundNumber} Not Open</h3>
        <p className="text-sm text-gray-500 mt-1">Submissions for this round are currently closed</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`round${roundNumber}-form`}>
      {/* GitHub URL */}
      <div>
        <label className="block font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Github size={18} className="text-gray-700" />
          GitHub Repository URL *
        </label>
        <input
          type="url"
          value={formData.github_url}
          onChange={(e) => setFormData(prev => ({ ...prev, github_url: e.target.value }))}
          placeholder="https://github.com/username/repository"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          data-testid={`github-url-${roundNumber}`}
        />
      </div>

      {/* Demo URL */}
      <div>
        <label className="block font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Globe size={18} className="text-green-600" />
          Working Demo/Project URL (Optional)
        </label>
        <input
          type="url"
          value={formData.demo_url}
          onChange={(e) => setFormData(prev => ({ ...prev, demo_url: e.target.value }))}
          placeholder="https://your-demo-link.com"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
          data-testid={`demo-url-${roundNumber}`}
        />
      </div>

      {/* Video URL */}
      <div>
        <label className="block font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Video size={18} className="text-red-600" />
          Video URL (Google Drive / YouTube) *
        </label>
        <input
          type="url"
          value={formData.video_url}
          onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
          placeholder="https://drive.google.com/... or https://youtube.com/..."
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
          data-testid={`video-url-${roundNumber}`}
        />
        <p className="text-xs text-gray-500 mt-1">Accepted: Google Drive, YouTube links</p>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className={`w-full py-3 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
          roundNumber === 3 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        data-testid={`submit-round${roundNumber}-btn`}
      >
        {submitting ? (
          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <>
            <CheckCircle size={20} />
            Submit Round {roundNumber} {roundNumber === 3 && '(Final)'}
          </>
        )}
      </button>
    </div>
  );
};

// Dynamic Form Component - Renders fields based on admin configuration
const DynamicRoundForm = ({ roundNumber, customFields, team, onSubmit, submission, roundOpen, previousRoundComplete }) => {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Already submitted - show submitted state
  if (submission) {
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
        <CheckCircle className="mx-auto text-green-600 mb-3" size={32} />
        <h3 className="font-bold text-green-800">Round {roundNumber} Submitted</h3>
        <p className="text-sm text-green-600 mt-1">Your submission has been recorded</p>
      </div>
    );
  }

  // Previous round not complete (for R2, R3)
  if (roundNumber > 1 && !previousRoundComplete) {
    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
        <Lock className="mx-auto text-amber-600 mb-3" size={32} />
        <h3 className="font-bold text-amber-800">Round {roundNumber - 1} Required</h3>
        <p className="text-sm text-amber-600 mt-1">Complete Round {roundNumber - 1} to unlock this submission</p>
      </div>
    );
  }

  // Round not open - show closed
  if (!roundOpen) {
    return (
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
        <Clock className="mx-auto text-gray-400 mb-3" size={32} />
        <h3 className="font-bold text-gray-600">Round {roundNumber} Not Open</h3>
        <p className="text-sm text-gray-500 mt-1">Submissions for this round are currently closed</p>
      </div>
    );
  }

  // No custom fields configured
  if (!customFields || customFields.length === 0) {
    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
        <AlertCircle className="mx-auto text-amber-600 mb-3" size={32} />
        <h3 className="font-bold text-amber-800">No Fields Configured</h3>
        <p className="text-sm text-amber-600 mt-1">The admin has not configured submission fields for this round yet</p>
      </div>
    );
  }

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleMultiSelectChange = (fieldName, option, checked) => {
    setFormData(prev => {
      const current = prev[fieldName] || [];
      if (checked) {
        return { ...prev, [fieldName]: [...current, option] };
      } else {
        return { ...prev, [fieldName]: current.filter(o => o !== option) };
      }
    });
  };

  const handleSubmit = async () => {
    // Validate required fields
    for (const field of customFields) {
      const value = formData[field.field_name];
      if (field.is_required && (value === undefined || value === '' || (Array.isArray(value) && value.length === 0))) {
        toast.error(`"${field.field_label}" is required`);
        return;
      }
      if (field.min_length && typeof value === 'string' && value.length < field.min_length) {
        toast.error(`"${field.field_label}" must be at least ${field.min_length} characters`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/team/submit/dynamic/${roundNumber}`, formData);
      toast.success(`Round ${roundNumber} submitted successfully!`);
      onSubmit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.field_name] || '';

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'textarea':
        return (
          <div>
            <textarea
              value={value}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
              placeholder={field.placeholder}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              data-testid={`field-${field.field_name}`}
            />
            {(field.min_length || field.max_length) && (
              <p className="text-xs text-gray-500 mt-1">
                {value.length}{field.max_length && `/${field.max_length}`} characters
                {field.min_length && ` (min: ${field.min_length})`}
              </p>
            )}
          </div>
        );

      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || 'https://...'}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || 'email@example.com'}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid={`field-${field.field_name}`}
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="space-y-2">
            {field.options?.map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(formData[field.field_name] || []).includes(opt)}
                  onChange={(e) => handleMultiSelectChange(field.field_name, opt, e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleFieldChange(field.field_name, e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded"
              data-testid={`field-${field.field_name}`}
            />
            <span className="text-sm text-gray-700">{field.placeholder || 'Yes'}</span>
          </label>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
          />
        );
    }
  };

  return (
    <div className="space-y-6" data-testid={`dynamic-form-round${roundNumber}`}>
      {customFields.map((field) => (
        <div key={field.id || field.field_name}>
          <label className="block font-semibold text-gray-800 mb-2">
            {field.field_label}
            {field.is_required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {renderField(field)}
          {field.help_text && (
            <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
          )}
        </div>
      ))}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className={`w-full py-3 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
          roundNumber === 3 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        data-testid={`submit-dynamic-round${roundNumber}-btn`}
      >
        {submitting ? (
          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <>
            <CheckCircle size={20} />
            Submit Round {roundNumber} {roundNumber === 3 && '(Final)'}
          </>
        )}
      </button>
    </div>
  );
};

// Submission Details View
const SubmissionDetails = ({ submission, roundNumber, customFields }) => {
  const [expanded, setExpanded] = useState(false);

  if (!submission) return null;

  // Check if this is a dynamic submission
  const isDynamic = submission.is_dynamic && submission.fields_data;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="text-green-600" size={20} />
          <span className="font-medium text-gray-800">View Submission Details</span>
        </div>
        {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      
      {expanded && (
        <div className="p-4 border-t border-gray-200 space-y-3">
          {isDynamic ? (
            // Dynamic submission - show fields_data
            <>
              {customFields?.map(field => {
                const value = submission.fields_data?.[field.field_name];
                if (value === undefined || value === null || value === '') return null;
                
                return (
                  <div key={field.field_name}>
                    <p className="text-xs font-medium text-gray-500 uppercase">{field.field_label}</p>
                    {Array.isArray(value) ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {value.map(v => (
                          <span key={v} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{v}</span>
                        ))}
                      </div>
                    ) : field.field_type === 'url' ? (
                      <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1">
                        {value} <ExternalLink size={12} />
                      </a>
                    ) : field.field_type === 'checkbox' ? (
                      <p className="text-sm text-gray-700 mt-1">{value ? 'Yes' : 'No'}</p>
                    ) : (
                      <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{value}</p>
                    )}
                  </div>
                );
              })}
            </>
          ) : roundNumber === 1 ? (
            // Legacy Round 1 format
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Business Scope</p>
                <p className="text-sm text-gray-700 mt-1">{submission.business_scope}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Social Impact</p>
                <p className="text-sm text-gray-700 mt-1">{submission.social_impact}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Tech Stack</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {submission.tech_stack?.map(tech => (
                    <span key={tech} className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </>
          ) : (
            // Legacy Round 2/3 format
            <>
              {submission.github_url && (
                <a
                  href={submission.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                >
                  <Github size={16} /> GitHub Repository <ExternalLink size={14} />
                </a>
              )}
              {submission.demo_url && (
                <a
                  href={submission.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-600 hover:text-green-800 text-sm"
                >
                  <Globe size={16} /> Demo Link <ExternalLink size={14} />
                </a>
              )}
              {submission.video_url && (
                <a
                  href={submission.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-red-600 hover:text-red-800 text-sm"
                >
                  <Video size={16} /> Video Link <ExternalLink size={14} />
                </a>
              )}
            </>
          )}
          <p className="text-xs text-gray-400">
            Submitted: {new Date(submission.submitted_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Component
const TeamSubmissionSection = ({ team }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProblemDetails, setShowProblemDetails] = useState(false);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/team/submission-status`);
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch submission status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const problemLocked = status?.problem_locked;
  const submissions = status?.submissions || {};
  const roundsConfig = status?.rounds_config || {};
  const canSubmit = status?.can_submit || {};
  const customFields = status?.custom_fields || {};
  const teamMembers = status?.members || [];

  const getRoundStatus = (roundNum) => {
    const roundKey = String(roundNum);
    if (submissions[roundKey]) return 'completed';
    if (canSubmit[roundKey]) return 'open';
    if (roundsConfig[roundKey]?.allow_submissions) {
      if (roundNum > 1 && !submissions[String(roundNum - 1)]) return 'locked';
      return 'open';
    }
    return 'closed';
  };

  return (
    <div className="space-y-6" data-testid="team-submission-section">
      {/* Problem Statement Details Modal */}
      {showProblemDetails && status?.problem_statement && (
        <ProblemStatementDetailModal
          problem={status.problem_statement}
          onClose={() => setShowProblemDetails(false)}
          onSelect={() => {}}
          isSelected={true}
        />
      )}

      {/* Problem Statement Selection */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Target className="text-blue-600" size={20} />
            Step 1: Problem Statement
          </h3>
        </div>
        <div className="p-6">
          {problemLocked ? (
            <div className="space-y-4" data-testid="locked-problem-statement">
              {/* Locked Status Banner */}
              <div className="flex items-center gap-3 p-3 bg-green-100 rounded-xl border border-green-200">
                <div className="p-2 bg-green-200 rounded-lg">
                  <CheckCircle className="text-green-700" size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-green-800">Problem Statement Confirmed</h4>
                  <p className="text-sm text-green-600">Your selection is locked and cannot be changed</p>
                </div>
              </div>

              {/* Problem Statement Card */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border-2 border-blue-200 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white leading-tight">
                        {status.problem_statement?.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="px-3 py-1 bg-white/20 backdrop-blur text-white rounded-full text-sm font-medium">
                          {status.problem_statement?.theme}
                        </span>
                        {status.problem_statement?.difficulty && (
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            status.problem_statement.difficulty === 'hard' 
                              ? 'bg-red-500/80 text-white' 
                              : status.problem_statement.difficulty === 'medium' 
                                ? 'bg-yellow-500/80 text-white' 
                                : 'bg-green-500/80 text-white'
                          }`}>
                            {status.problem_statement.difficulty.charAt(0).toUpperCase() + status.problem_statement.difficulty.slice(1)} Difficulty
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowProblemDetails(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur text-white rounded-lg transition-colors text-sm font-medium"
                      data-testid="view-problem-details-btn"
                    >
                      <Eye size={16} />
                      View Full Details
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="p-6">
                  <div className="space-y-4">
                    {/* Problem Description */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={16} className="text-blue-600" />
                        <h5 className="font-semibold text-gray-800">Problem Description</h5>
                      </div>
                      <p className="text-gray-700 leading-relaxed bg-white p-4 rounded-lg border border-gray-200">
                        {status.problem_statement?.description}
                      </p>
                    </div>

                    {/* Scope - if available */}
                    {status.problem_statement?.scope && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Target size={16} className="text-purple-600" />
                          <h5 className="font-semibold text-gray-800">Scope</h5>
                        </div>
                        <p className="text-gray-700 leading-relaxed bg-purple-50 p-4 rounded-lg border border-purple-200">
                          {status.problem_statement?.scope}
                        </p>
                      </div>
                    )}

                    {/* Requirements - if available */}
                    {status.problem_statement?.requirements && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle size={16} className="text-green-600" />
                          <h5 className="font-semibold text-gray-800">Requirements</h5>
                        </div>
                        <p className="text-gray-700 leading-relaxed bg-green-50 p-4 rounded-lg border border-green-200 whitespace-pre-wrap">
                          {status.problem_statement?.requirements}
                        </p>
                      </div>
                    )}

                    {/* Expected Outcomes - if available */}
                    {status.problem_statement?.expected_outcomes && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Rocket size={16} className="text-orange-600" />
                          <h5 className="font-semibold text-gray-800">Expected Outcomes</h5>
                        </div>
                        <p className="text-gray-700 leading-relaxed bg-orange-50 p-4 rounded-lg border border-orange-200 whitespace-pre-wrap">
                          {status.problem_statement?.expected_outcomes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ProblemStatementSelector team={team} onSelect={fetchStatus} disabled={problemLocked} />
          )}
        </div>
      </div>

      {/* Round Submissions */}
      {[1, 2, 3].map((roundNum) => {
        const roundKey = String(roundNum);
        const roundConfig = roundsConfig[roundKey] || {};
        const roundStatus = getRoundStatus(roundNum);
        const submission = submissions[roundKey];

        const statusColors = {
          completed: 'border-green-200 bg-green-50',
          open: 'border-blue-200 bg-blue-50',
          locked: 'border-amber-200 bg-amber-50',
          closed: 'border-gray-200 bg-gray-50'
        };

        const statusIcons = {
          completed: <CheckCircle className="text-green-600" size={20} />,
          open: <Rocket className="text-blue-600" size={20} />,
          locked: <Lock className="text-amber-600" size={20} />,
          closed: <AlertCircle className="text-gray-400" size={20} />
        };

        return (
          <div 
            key={roundNum} 
            className={`bg-white rounded-xl border-2 shadow-sm overflow-hidden ${
              roundStatus === 'completed' ? 'border-green-300' :
              roundStatus === 'open' && problemLocked ? 'border-blue-300' :
              'border-gray-200'
            }`}
          >
            <div className={`px-6 py-4 border-b ${statusColors[roundStatus]}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  {statusIcons[roundStatus]}
                  Round {roundNum}: {roundNum === 1 ? 'Basic Details' : roundNum === 2 ? 'Technical Submission' : 'Final Submission'}
                </h3>
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                  roundStatus === 'completed' ? 'bg-green-200 text-green-800' :
                  roundStatus === 'open' ? 'bg-blue-200 text-blue-800' :
                  roundStatus === 'locked' ? 'bg-amber-200 text-amber-800' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {roundStatus === 'completed' ? 'Submitted' :
                   roundStatus === 'open' ? 'Open' :
                   roundStatus === 'locked' ? 'Locked' :
                   'Closed'}
                </span>
              </div>
              {roundConfig.description && (
                <p className="text-sm text-gray-600 mt-1">{roundConfig.description}</p>
              )}
            </div>
            
            <div className="p-6">
              {!problemLocked && roundNum === 1 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <Lock className="mx-auto text-amber-600 mb-2" size={24} />
                  <p className="text-amber-800 font-medium">Select a problem statement first</p>
                </div>
              ) : submission ? (
                <SubmissionDetails submission={submission} roundNumber={roundNum} customFields={customFields[roundKey]} />
              ) : customFields[roundKey] && customFields[roundKey].length > 0 ? (
                // Use dynamic form when custom fields are configured
                <DynamicRoundForm
                  roundNumber={roundNum}
                  customFields={customFields[roundKey]}
                  team={{ ...team, members: teamMembers }}
                  onSubmit={fetchStatus}
                  submission={submission}
                  roundOpen={roundsConfig[roundKey]?.allow_submissions}
                  previousRoundComplete={roundNum === 1 ? true : !!submissions[String(roundNum - 1)]}
                />
              ) : roundNum === 1 ? (
                // Legacy Round 1 form (when no custom fields)
                <Round1Form 
                  team={{ ...team, members: teamMembers }}
                  onSubmit={fetchStatus} 
                  submission={submission}
                  roundOpen={roundsConfig['1']?.allow_submissions}
                />
              ) : (
                // Legacy Round 2/3 form (when no custom fields)
                <TechnicalSubmissionForm
                  roundNumber={roundNum}
                  onSubmit={fetchStatus}
                  submission={submission}
                  roundOpen={roundsConfig[roundKey]?.allow_submissions}
                  previousRoundComplete={!!submissions[String(roundNum - 1)]}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TeamSubmissionSection;
